import { cn } from "@-/com.npmjs.cn";
import { ScrollView, View } from "@-/com.npmjs.react-native";
import { IconButton } from "@-/dev.improve.haniverse.components.ui.button.icon";
import {
  ToolbarExpandingPopover,
  ToolbarIconButton,
  ToolbarMenuContext,
  type ToolbarIconButtonProps,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import {
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  type ContextType,
  type ReactNode,
} from "react";
import { ToolbarPopoverContext } from "#toolbar-popover-context";

export type ToolbarPopoverButtonProps = Pick<
  ToolbarIconButtonProps,
  | "accessibilityLabel"
  | "className"
  | "disabled"
  | "iconColor"
  | "iconSize"
  | "isLoading"
  | "label"
  | "name"
  | "testID"
> & {
  /** The popover body — arbitrary UI, typically the `ToolbarPopover*` content primitives. */
  children: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  /** Paints the pill with the accent fill, for a pill whose current choice is non-default. */
  selected?: boolean;
};

/** Toolbar option menus share the account and More buttons' full-width floating surface. */
export function ToolbarPopoverButton(props: ToolbarPopoverButtonProps) {
  const menu = useContext(ToolbarMenuContext);
  return menu === null ? (
    <StandaloneToolbarPopoverButton {...props} />
  ) : (
    <ToolbarMenuPopoverButton {...props} menu={menu} />
  );
}

type ToolbarMenu = NonNullable<ContextType<typeof ToolbarMenuContext>>;

type ToolbarMenuPopoverTabGate = Tagged<
  { id: string; isView: boolean },
  "ToolbarMenuPopoverTabGate"
>;

/**
 * Whether this popover draws its own tab in the menu. The page layout has no tabs: every section
 * shows at once under the menu title. In the tabs layout, the View / Filters / Sort popovers
 * collapse into ONE shared "View" tab, drawn only by the popover whose id the menu picked as
 * `viewId`; the others still register their content, but draw nothing (`null`).
 */
function getToolbarMenuPopoverTabGate({
  id,
  isView,
  layout,
  viewId,
}: {
  id: string;
  isView: boolean;
  layout: ToolbarMenu["layout"];
  viewId: ToolbarMenu["viewId"];
}): ToolbarMenuPopoverTabGate | null {
  return layout === "page" || (isView && id !== viewId)
    ? null
    : createGate<ToolbarMenuPopoverTabGate>({ id, isView });
}

/**
 * Registers the popover's content with the enclosing `ToolbarMoreActions` menu. Stays mounted for
 * as long as the popover exists — even when another View popover draws the shared tab — because its
 * layout effects own the registration.
 */
function ToolbarMenuPopoverButton({
  children,
  disabled: disabledProp = false,
  menu,
  onOpenChange,
  selected: _selected,
  ...props
}: ToolbarPopoverButtonProps & { menu: ToolbarMenu }) {
  const { activeId, close, isOpen, layout, register, unregister, viewId } = menu;
  const disabled = disabledProp ?? false;
  const id = useId();
  const isView = props.label === "View" || props.label === "Filters" || props.label === "Sort";
  // The page layout shows every enabled section whenever the menu is open.
  const isActive =
    layout === "page" ? !disabled : activeId === id || (isView && activeId === viewId);
  useLayoutEffect(() => {
    register(id, {
      content: <ToolbarPopoverContext value={{ close, isOpen }}>{children}</ToolbarPopoverContext>,
      disabled,
      isView,
    });
  }, [children, close, disabled, id, isOpen, isView, register]);
  useLayoutEffect(() => () => unregister(id), [id, unregister]);
  useEffect(() => {
    if (isActive && isOpen) {
      onOpenChange?.(true);
      return () => onOpenChange?.(false);
    }
  }, [isActive, isOpen, onOpenChange]);

  const tabGate = getToolbarMenuPopoverTabGate({ id, isView, layout, viewId });
  return (
    <>
      {tabGate !== null && (
        <ToolbarMenuPopoverTab
          {...props}
          disabled={disabled}
          gate={tabGate}
          isActive={isActive}
          select={menu.select}
        />
      )}
    </>
  );
}

function ToolbarMenuPopoverTab({
  className,
  disabled,
  gate: { id, isView },
  isActive,
  select,
  ...props
}: Omit<ToolbarPopoverButtonProps, "children" | "onOpenChange" | "selected"> & {
  disabled: boolean;
  gate: ToolbarMenuPopoverTabGate;
  isActive: boolean;
  select: ToolbarMenu["select"];
}) {
  return (
    <ToolbarIconButton
      {...props}
      accessibilityLabel={isView ? "View" : props.accessibilityLabel}
      accessibilityRole="tab"
      accessibilityState={{ disabled, expanded: isActive, selected: isActive }}
      // oxlint-disable-next-line tailwindcss/no-contradicting-variants -- selected section tint
      className={cn(isActive && "bg-accent dark:bg-accent", className)}
      disabled={disabled}
      label={isView ? "View" : props.label}
      onPress={() => select(id)}
      role="tab"
    />
  );
}

function StandaloneToolbarPopoverButton({
  accessibilityLabel,
  children,
  className,
  disabled,
  iconColor,
  iconSize,
  isLoading,
  label,
  name,
  onOpenChange,
  selected = false,
  testID,
}: ToolbarPopoverButtonProps) {
  const { diameter } = useToolbarGridMetrics();
  const heading = label ?? accessibilityLabel ?? "";
  const bandHeight = diameter - 2;
  return (
    <ToolbarExpandingPopover
      backdropTestID={testID === undefined ? undefined : `${testID}-backdrop`}
      dismissLabel={`Dismiss ${heading}`}
      onOpenChange={onOpenChange}
      testID={testID === undefined ? undefined : `${testID}-popover`}
      trigger={({ isOpen, open }) => (
        <ToolbarIconButton
          accessibilityElementsHidden={isOpen}
          accessibilityLabel={accessibilityLabel}
          accessibilityState={{ disabled: disabled ?? false, expanded: isOpen, selected }}
          // oxlint-disable-next-line tailwindcss/no-contradicting-variants -- opaque selected surface
          className={cn(selected && "bg-accent dark:bg-accent", isOpen && "opacity-0", className)}
          disabled={disabled}
          iconColor={iconColor}
          iconSize={iconSize}
          importantForAccessibility={isOpen ? "no-hide-descendants" : "auto"}
          isLoading={isLoading}
          label={label}
          name={name}
          onPress={open}
          testID={testID}
        />
      )}
    >
      {({ close, isOpen, onContentSizeChange }) => (
        <>
          <View className="p-0.75" style={{ height: bandHeight }}>
            <IconButton
              accessibilityLabel={accessibilityLabel}
              accessibilityState={{ expanded: true, selected }}
              active
              className="size-full bg-muted"
              iconColor={iconColor}
              iconSize={iconSize}
              isLoading={isLoading}
              label={label}
              name={name}
              onPress={() => close()}
              testID={testID === undefined ? undefined : `${testID}-expanded`}
            />
          </View>
          <ScrollView
            className="flex-1"
            contentContainerClassName="gap-3 px-3 pb-3"
            keyboardShouldPersistTaps="handled"
            onContentSizeChange={(width, height) => onContentSizeChange(width, height + bandHeight)}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
          >
            <ToolbarPopoverContext value={{ close, isOpen }}>{children}</ToolbarPopoverContext>
          </ScrollView>
        </>
      )}
    </ToolbarExpandingPopover>
  );
}
